import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const MODERN_OPTIONS = [
  { value: '1m', label: '1 Month' },
  { value: '3m', label: '3 Months' },
  { value: '6m', label: '6 Months' },
  { value: '12m', label: '12 Months' },
];

const VINTAGE_OPTIONS = [
  { value: 'top3', label: 'Top 3 (12mo)' },
  { value: 'low3', label: 'Low 3 (12mo)' },
  { value: '12avg', label: '12 Month Median' },
  { value: 'alltime', label: 'All Time Median' },
];

const PricingPreferences = () => {
  const navigate = useNavigate();
  const { activeProfile, refreshProfile } = useProfile();
  const { isDark, theme } = useTheme();

  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [modernRange, setModernRange] = useState('3m');
  const [vintageMethod, setVintageMethod] = useState('top3');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeProfile) {
      setPricingEnabled(activeProfile.pricing_enabled === true);
      const prefs = activeProfile.pricing_prefs || {};
      setModernRange(prefs.modern_range || '3m');
      setVintageMethod(prefs.vintage_method || 'top3');
    }
  }, [activeProfile]);

  const handleTogglePricing = async () => {
    const newVal = !pricingEnabled;
    setPricingEnabled(newVal);
    if (activeProfile?.id) {
      await supabase
        .from('users')
        .update({ pricing_enabled: newVal })
        .eq('id', activeProfile.id);
      await refreshProfile();
    }
  };

  const handleSavePrefs = async (field, value) => {
    if (!activeProfile?.id) return;
    setSaving(true);

    const newPrefs = {
      modern_range: field === 'modern_range' ? value : modernRange,
      vintage_method: field === 'vintage_method' ? value : vintageMethod,
    };

    if (field === 'modern_range') setModernRange(value);
    if (field === 'vintage_method') setVintageMethod(value);

    await supabase
      .from('users')
      .update({ pricing_prefs: newPrefs })
      .eq('id', activeProfile.id);

    await refreshProfile();
    setSaving(false);
  };

  const Toggle = ({ value, onChange }) => (
    <button
      style={{
        width: 48, height: 28, borderRadius: 14, border: 'none',
        cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s',
        padding: 0, flexShrink: 0,
        backgroundColor: value ? theme.accent : theme.border,
      }}
      onClick={onChange}
    >
      <div style={{
        width: 24, height: 24, borderRadius: 12, backgroundColor: '#fff',
        position: 'absolute', top: 2, left: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'transform 0.2s',
        transform: value ? 'translateX(20px)' : 'translateX(0)',
      }} />
    </button>
  );

  const PillGroup = ({ options, selected, onChange }) => (
    <div style={styles.pillGroup}>
      {options.map(opt => (
        <button
          key={opt.value}
          style={{
            ...styles.pill,
            ...(selected === opt.value ? styles.pillActive : {}),
          }}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      backgroundColor: theme.bgCard,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      borderBottom: `1px solid ${theme.border}`,
    },
    backButton: {
      background: 'none', border: 'none', padding: 8, cursor: 'pointer',
      color: theme.accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    headerTitle: {
      fontSize: 18, fontWeight: 600, color: theme.text, margin: 0,
    },
    content: {
      flex: 1, padding: 16, maxWidth: 600, margin: '0 auto', width: '100%',
    },
    sectionHeader: {
      fontSize: 11, fontWeight: 600, color: theme.textMuted,
      textTransform: 'uppercase', letterSpacing: 1, margin: '20px 0 10px 0',
    },
    section: {
      backgroundColor: theme.bgCard, borderRadius: 12, padding: 16,
      marginBottom: 12, border: `1px solid ${theme.border}`,
    },
    settingRow: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 0',
    },
    settingInfo: {
      display: 'flex', flexDirection: 'column', gap: 2,
    },
    settingLabel: {
      fontSize: 14, fontWeight: 500, color: theme.text,
    },
    settingDesc: {
      fontSize: 12, color: theme.textMuted,
    },
    pillGroup: {
      display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8,
    },
    pill: {
      padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500,
      border: `1px solid ${theme.border}`, backgroundColor: 'transparent',
      color: theme.text, cursor: 'pointer', transition: 'all 0.2s',
    },
    pillActive: {
      backgroundColor: theme.accent, color: '#fff',
      border: `1px solid ${theme.accent}`,
    },
    note: {
      fontSize: 12, color: theme.textMuted, lineHeight: 1.5,
      marginTop: 12, padding: '10px 12px', borderRadius: 8,
      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Pricing Preferences</h1>
      </div>

      <div style={styles.content}>
        {/* Show Prices Toggle */}
        <h4 style={styles.sectionHeader}>Display</h4>
        <div style={styles.section}>
          <div style={styles.settingRow}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Show Prices</span>
              <span style={styles.settingDesc}>
                {pricingEnabled ? 'Estimated values shown on cards' : 'Card prices hidden'}
              </span>
            </div>
            <Toggle value={pricingEnabled} onChange={handleTogglePricing} />
          </div>
        </div>

        {pricingEnabled && (
          <>
            {/* Modern Cards */}
            <h4 style={styles.sectionHeader}>Modern Cards (2017+)</h4>
            <div style={styles.section}>
              <div style={styles.settingInfo}>
                <span style={styles.settingLabel}>Price Range</span>
                <span style={styles.settingDesc}>
                  Average sold price over this time period
                </span>
              </div>
              <PillGroup
                options={MODERN_OPTIONS}
                selected={modernRange}
                onChange={(v) => handleSavePrefs('modern_range', v)}
              />
            </div>

            {/* Vintage Cards */}
            <h4 style={styles.sectionHeader}>Vintage Cards (Pre-2017)</h4>
            <div style={styles.section}>
              <div style={styles.settingInfo}>
                <span style={styles.settingLabel}>Pricing Method</span>
                <span style={styles.settingDesc}>
                  How to calculate vintage card values
                </span>
              </div>
              <PillGroup
                options={VINTAGE_OPTIONS}
                selected={vintageMethod}
                onChange={(v) => handleSavePrefs('vintage_method', v)}
              />
            </div>

            {/* Info Note */}
            <div style={styles.note}>
              Modern vs Vintage is auto-detected per card based on the set name.
              Sun & Moon era and newer are Modern. Everything before is Vintage.
              Prices come from real eBay sold listings.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PricingPreferences;
